import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import routesConfig from 'constants/routesConfig.json';
import { TAB_SLUG, TabSlug, isTabSlug, ECoachView } from 'constants/ecoachTabs';

// hooks
import { usePosthogCapture } from 'eCoach/posthogCapture/chat';

// assets
import { Communication, General } from 'icons/untitledui';

// components
import DemoDialog from 'eCoach/allChats/DemoDialog';
import NavRow from 'eCoach/allChats/NavRow';

// context
import { useUser } from 'shared/hooks/api/user';

// styles
import * as Buttons from 'theme/components/Buttons.style';
import * as PageLayoutStyles from 'layout/styles/PageLayout.style';
import * as NavRowStyles from 'eCoach/_styles/NavRow.style';
import * as Styled from 'eCoach/_styles/AllChats.style';
import * as Text from 'theme/components/Text.style';

interface StatusTabsProps {
  activeView: ECoachView;
  setActiveView: (session: ECoachView) => void;
  isDesktopView: boolean;
}

interface UserData {
  is_team_lead?: boolean;
  is_demo?: boolean;
  is_internal?: boolean;
}

export default function StatusTabs({
  activeView,
  setActiveView,
  isDesktopView,
}: StatusTabsProps): React.ReactElement {
  const { t } = useTranslation('pageEcoach');
  const history = useHistory();
  const chat = usePosthogCapture();
  const { data: user } = useUser() as { data: UserData };

  const [isDemoDialogOpen, setIsDemoDialogOpen] = useState(false);

  const closeDemoModal = () => setIsDemoDialogOpen(false);
  const newConversation = useCallback(() => {
    setActiveView('none');
    if (user.is_demo && !user.is_internal) setIsDemoDialogOpen(true);
    else history.push(`${routesConfig.CONVO_PAGE}`);
  }, [history, user, setActiveView]);

  // Define stable slugs and derive translated labels
  const tabs: Array<{
    value: TabSlug;
    label: string;
    id: string;
    'aria-controls': string;
    icon: React.ReactNode;
  }> = [
    {
      value: TAB_SLUG.ALL,
      label: t('allChats.StatusTabs.All conversations'),
      id: 'all-conversations-tab',
      'aria-controls': 'all-conversations-tabpanel',
      icon: <Communication.MessageChatSquare size={24} />,
    },
    {
      value: TAB_SLUG.DRAFTS,
      label: t('allChats.StatusTabs.Drafts'),
      id: 'drafts-tab',
      'aria-controls': 'drafts-tabpanel',
      icon: <Communication.MessageDotsSquare size={24} />,
    },
    {
      value: TAB_SLUG.IN_PROGRESS,
      label: t('allChats.StatusTabs.In progress'),
      id: 'in-progress-tab',
      'aria-controls': 'in-progress-tabpanel',
      icon: <Communication.MessageTextSquare02 size={24} />,
    },
    {
      value: TAB_SLUG.RESOLVED,
      label: t('allChats.StatusTabs.Resolved'),
      id: 'resolved-tab',
      'aria-controls': 'resolved-tabpanel',
      icon: <Communication.MessageCheckSquare size={24} />,
    },
  ];

  const captureTabClick = useCallback(
    (slug: TabSlug) => {
      switch (slug) {
        case TAB_SLUG.ALL:
          chat.tabsSelectedAllConversations();
          break;
        case TAB_SLUG.DRAFTS:
          chat.tabsSelectedDrafts();
          break;
        case TAB_SLUG.IN_PROGRESS:
          chat.tabsSelectedInProgress();
          break;
        case TAB_SLUG.RESOLVED:
          chat.tabsSelectedResolved();
          break;
        default:
          break;
      }
    },
    [chat]
  );

  const handleTabChange = useCallback(
    (slug: TabSlug) => {
      setActiveView(slug);
      captureTabClick(slug);
      history.push(`${routesConfig.ALL_CONVOS_PAGE}/${slug}`);
    },
    [history, setActiveView, captureTabClick]
  );

  // Determine currently active slug, tolerate legacy translated values
  const activeSlug: TabSlug = isTabSlug(activeView)
    ? (activeView as TabSlug)
    : TAB_SLUG.ALL;

  return (
    <NavRowStyles.Container>
      {isDesktopView ? (
        <NavRowStyles.PageTitle>
          <Text.H5>Conversations</Text.H5>
        </NavRowStyles.PageTitle>
      ) : (
        <PageLayoutStyles.PageTitleContainer>
          <Text.H3>Conversations</Text.H3>
        </PageLayoutStyles.PageTitleContainer>
      )}

      <Styled.NewConvoButton>
        <Buttons.PrimaryIcon medium onClick={newConversation}>
          <General.Plus size={24} />
          {t('allChats.New conversation')}
        </Buttons.PrimaryIcon>

        <DemoDialog
          closeModal={closeDemoModal}
          isOpenConfirmDialog={isDemoDialogOpen}
        />
      </Styled.NewConvoButton>

      {tabs?.map((tab) => (
        <NavRow
          key={tab?.id}
          heading={tab?.label}
          onClickAction={() => {
            handleTabChange(tab?.value);
          }}
          subheading=""
          icon={tab?.icon}
          isActive={activeSlug === tab?.value}
        />
      ))}
    </NavRowStyles.Container>
  );
}
